/**
 * School outreach microsites (/school/:slug).
 *
 * All school-specific copy lives here; `SchoolLandingPage` is the only layout.
 * To add a school, append an entry to SCHOOLS — the route, static HTML head
 * (see vite.config.ts) and Netlify rewrite are generated from this list.
 *
 * This file is also imported by vite.config.ts, so keep it free of browser-only
 * APIs and `import.meta.env`.
 */

export const SITE_ORIGIN = 'https://helpfromsky.com';
export const SITE_NAME = 'Help from Sky';
export const CONTACT_EMAIL = 'hello@helpfromsky.com';
export const APP_STORE_URL = 'https://apps.apple.com/us/app/help-from-sky/id6758615690';
export const PRIVACY_PATH = '/privacy.html';
export const SOCIAL_IMAGE_PATH = '/help-from-sky-logo.png';
/** Brand fonts from public/landingpage.html (the SPA shell loads no web fonts). */
export const SCHOOL_FONT_HREF =
  'https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=Fraunces:ital,opsz,wght@0,9..144,600;0,9..144,700;1,9..144,600&display=swap';

export interface SchoolProgram {
  name: string;
  /**
   * Only verified programs are rendered. Flip to true once confirmed with the
   * school or its official website — third-party directories don't count.
   */
  verified: boolean;
  source?: string;
}

export interface SchoolConfig {
  slug: string;
  name: string;
  shortName: string;
  /** City and state only — never publish a street address on these pages. */
  location: string;
  programs: SchoolProgram[];
  /** Section C: why we're reaching out to this school. */
  intro: string;
  /** Beta section: school-specific note to students. */
  studentMessage: string;
  /** Partnership section: school-specific note to administrators/instructors. */
  administratorMessage: string;
  /** Lead-in line above the partnership CTA. */
  contactCta: string;
  /** Stored on every submission and analytics event from this page. */
  referralCode: string;
  metadata: {
    title: string;
    description: string;
  };
}

/**
 * Beta offer details. Rendered only when non-null — do not fill these in until
 * the offer is confirmed.
 */
export const BETA_PROGRAM: {
  offer: string | null;
  eligibility: string | null;
  timeCommitment: string | null;
  cost: string | null;
} = {
  offer: null,
  eligibility: null,
  timeCommitment: null,
  cost: null,
};

/** About section founder/contact intro. Rendered only when set and verified. */
export const FOUNDER_INTRO: { name: string; role: string; message: string } | null = null;

export const SCHOOLS: SchoolConfig[] = [
  {
    slug: 'tspa-dc',
    // Federal (IPEDS) listing name: "The Salon Professional Academy-Washington DC".
    name: 'The Salon Professional Academy DC',
    shortName: 'TSPA DC',
    location: 'Washington, DC',
    programs: [
      { name: 'Cosmetology', verified: false, source: 'IPEDS via third-party directories' },
    ],
    intro:
      "We're reaching out to The Salon Professional Academy DC because students preparing for careers in beauty may also be thinking about how to manage their first clients and build an independent practice.",
    studentMessage:
      "Finishing your hours is a big milestone—and so is booking your first paying client. If you're getting close to that transition, we'd love your help shaping simple tools for keeping appointments and client details organized from day one.",
    administratorMessage:
      'For educators focused on career readiness, this is an optional, low-effort way to point students toward the business side of a beauty career as they move from training to taking clients.',
    contactCta:
      'Work with students at The Salon Professional Academy DC? We’d welcome a short conversation.',
    referralCode: 'tspa-dc',
    metadata: {
      title: 'Beauty Student Beta Program | The Salon Professional Academy DC | Help from Sky',
      description:
        'An invitation for beauty students at The Salon Professional Academy DC to help shape Help from Sky, simple software for managing appointments and clients.',
    },
  },
  {
    slug: 'dudley-beauty-college',
    name: 'Dudley Beauty College',
    shortName: 'Dudley',
    location: 'Washington, DC',
    // No program list confirmed yet — add entries once verified with the school.
    programs: [],
    intro:
      "We'd love to connect with Dudley Beauty College students who are interested in turning their beauty skills into independent businesses and developing the habits needed to manage clients and appointments.",
    studentMessage:
      "Great work keeps clients coming back; good habits keep a business running. If you're planning to turn your skills into your own business, we'd love to hear what managing clients and appointments looks like for you.",
    administratorMessage:
      'If your students ask about the practical side of going independent—keeping a schedule, tracking clients, following up—this is an optional resource you can pass along.',
    contactCta:
      'Part of the Dudley Beauty College team? We’d love to hear what would be useful for your students.',
    referralCode: 'dudley-beauty-college',
    metadata: {
      title: 'Beauty Student Beta Program | Dudley Beauty College | Help from Sky',
      description:
        'An invitation for Dudley Beauty College students planning independent beauty businesses to help shape simple software for managing clients and appointments.',
    },
  },
  {
    slug: 'bennett-career-institute',
    name: 'Bennett Career Institute',
    shortName: 'Bennett',
    location: 'Washington, DC',
    programs: [
      { name: 'Cosmetology', verified: false, source: 'IPEDS via third-party directories' },
      { name: 'Barbering', verified: false, source: 'IPEDS via third-party directories' },
    ],
    intro:
      'Help from Sky is looking to connect with Bennett Career Institute students preparing to build a clientele, work independently, or eventually operate their own service business.',
    studentMessage:
      "If you're in a beauty-related program and planning to build a clientele or work independently, your perspective can help shape a tool designed for solo service professionals.",
    administratorMessage:
      'This opportunity is intended for students in beauty-related programs who plan to take their own clients, so it can be shared with just the students it fits.',
    contactCta: 'Part of Bennett Career Institute? Let’s talk about whether this fits your students.',
    referralCode: 'bennett-career-institute',
    metadata: {
      title: 'Beauty Student Beta Program | Bennett Career Institute | Help from Sky',
      description:
        'An invitation for Bennett Career Institute students in beauty-related programs to help shape simple software for managing appointments and clients.',
    },
  },
  {
    slug: 'montgomery-beauty-school',
    name: 'Montgomery Beauty School',
    shortName: 'Montgomery',
    location: 'Silver Spring, MD',
    programs: [
      { name: 'Cosmetology', verified: false, source: 'IPEDS via third-party directories' },
      { name: 'Barbering', verified: false, source: 'IPEDS via third-party directories' },
    ],
    intro:
      "We're inviting Montgomery Beauty School students and recent graduates who are preparing to take clients to help shape a simple business-management tool for independent beauty professionals.",
    studentMessage:
      "As a beta tester, you'd be among the first to try what we're building and share feedback directly with the team building it. Tell us what works, what doesn't, and what you wish existed.",
    administratorMessage:
      'For students and recent graduates preparing to take clients, this is a chance to give direct feedback on a product built for solo professionals—an optional resource you’re welcome to share.',
    contactCta: 'Part of the Montgomery Beauty School team? We’d be glad to connect.',
    referralCode: 'montgomery-beauty-school',
    metadata: {
      title: 'Beauty Student Beta Program | Montgomery Beauty School | Help from Sky',
      description:
        'An invitation for Montgomery Beauty School students and recent graduates to get early access to, and give feedback on, software built for solo beauty pros.',
    },
  },
];

export function getSchoolBySlug(slug: string | undefined): SchoolConfig | undefined {
  return SCHOOLS.find(s => s.slug === slug);
}

export function schoolPath(slug: string): string {
  return `/school/${slug}`;
}

export function schoolCanonicalUrl(slug: string): string {
  return `${SITE_ORIGIN}${schoolPath(slug)}`;
}

/** Head tags shared by the runtime hook and the build-time static HTML. */
export function schoolHeadMeta(school: SchoolConfig) {
  const url = schoolCanonicalUrl(school.slug);
  return {
    title: school.metadata.title,
    canonical: url,
    meta: [
      { name: 'description', content: school.metadata.description },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: SITE_NAME },
      { property: 'og:title', content: school.metadata.title },
      { property: 'og:description', content: school.metadata.description },
      { property: 'og:url', content: url },
      { property: 'og:image', content: `${SITE_ORIGIN}${SOCIAL_IMAGE_PATH}` },
      { name: 'twitter:card', content: 'summary' },
      { name: 'twitter:title', content: school.metadata.title },
      { name: 'twitter:description', content: school.metadata.description },
    ] as Array<{ name?: string; property?: string; content: string }>,
  };
}
